import React, { useEffect, useState } from 'react';
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    addDays,
    isSameMonth,
    subMonths
} from 'date-fns';
import './Calendar.css';

interface Event {
    track_id: string;
    timestamp: number;
    listen_count?: number;
    duration_seconds?: number;
}

interface EventsByDate {
    [date: string]: Event[];
}

interface CalendarProps {
    selectedMonth: Date;
    userId: string;
    onDateClick: (date: Date) => void;
    user: any;
    refreshKey: number;
    events: EventsByDate;
    setEvents: React.Dispatch<React.SetStateAction<EventsByDate>>;
}

const Calendar: React.FC<CalendarProps> = ({ selectedMonth, userId, onDateClick, user, refreshKey, events, setEvents }) => {
    const [expandedEventKeys, setExpandedEventKeys] = useState<Set<string>>(new Set());

    const toggleEventExpand = (key: string) => {
        setExpandedEventKeys(prev => {
            const copy = new Set(prev);
            if (copy.has(key)) {
                copy.delete(key);
            } else {
                copy.add(key);
            }
            return copy;
        });
    };

    useEffect(() => {
        const fetchUserEvents = async () => {
            if (!user || !userId) return;

            const token = await user.getIdToken();
            const previousMonth = subMonths(selectedMonth, 1);
            const apiUrl = process.env.REACT_APP_API_URL;
            const authHeader = `Bearer ${token}`;
            const headers = { "Content-Type": "application/json", "Authorization": authHeader };

            const start = format(startOfMonth(previousMonth), 'yyyy-MM-dd');
            const end = format(endOfMonth(selectedMonth), 'yyyy-MM-dd');

            const response = await fetch(`${apiUrl}/progress-events?user_id=${userId}&start_date=${start}&end_date=${end}`, { headers });
            const data = await response.json();
            setEvents(data);
            const hasAnyEvents = Object.values(data).some(
                (arr) => Array.isArray(arr) && arr.length > 0
            );

            if (!hasAnyEvents) {
                alert('No user progress found for the selected period.');
            }
        };
        fetchUserEvents();
    }, [user, selectedMonth, userId, refreshKey, setEvents]);

    const renderHeader = () => (
        <div className="calendar-header">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div className="day-header" key={day}>{day}</div>
            ))}
        </div>
    );

    const renderDays = () => {
        const monthStart = startOfMonth(selectedMonth);
        const monthEnd = endOfMonth(monthStart);
        const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
        const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

        const days = [];
        let day = startDate;

        while (day <= endDate) {
            for (let i = 0; i < 7; i++) {
                const dateStr = format(day, 'yyyy-MM-dd');
                const dayEvents = Array.isArray(events[dateStr]) ? events[dateStr] : [];
                const currentDay = new Date(day);
                days.push(
                    <div
                        key={dateStr}
                        className={`day-cell ${!isSameMonth(day, monthStart) ? 'dimmed' : ''}`}
                        onClick={() => onDateClick(currentDay)}
                    >
                        <div className="day-number">{format(day, 'd')}</div>
                        {dayEvents.length > 0 && (
                            <div className="event-list">
                                {dayEvents.map((e, idx) => {
                                    const eventKey = `${dateStr}_${idx}`;
                                    const isExpanded = expandedEventKeys.has(eventKey);
                                    return (
                                        <div
                                            key={eventKey}
                                            className="event-item"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                toggleEventExpand(eventKey);
                                            }}
                                        >
                                            <div>
                                                <strong>🎵 {e.track_id}</strong>
                                            </div>
                                            {isExpanded && (
                                                <div className="event-details">
                                                    {e.listen_count !== undefined && <div><strong>Listens:</strong> {e.listen_count}</div>}
                                                    {e.duration_seconds !== undefined && <div><strong>Duration:</strong> {Math.round(e.duration_seconds)} sec</div>}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
                day = addDays(day, 1);
            }
        }
        return <div className="calendar-grid">{days}</div>;
    };

    return (
        <div className="calendar-container">
            {renderHeader()}
            {renderDays()}
        </div>
    );
};

export default Calendar;
